// ========================================
// APP.JS - ADAPTADO COM AUTENTICAÇÃO E PRIMEIRO ACESSO
// src/App.js
// ========================================

import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { FilePlus, ClipboardList, Users, BarChart3, Smile, UserCheck, LogOut, ShieldCheck, Home as HomeIcon, Settings, ChevronDown, Table2, Building2, Package, Trophy } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import PrimeiroAcesso from './components/PrimeiroAcesso';
import Footer from './components/Footer';
import NovoPedido from './NovoPedido';
import ConsultaPedidos from './ConsultaPedidos';
import Dashboard from './dashboard';
import Configuracoes from './components/Configuracoes';
import Clientes from './components/Clientes';
import Home from './components/Home';
import Loading from './components/Loading';
import Campanhas from './components/Campanhas';
import { ModalProvider } from './contexts/ModalContext';


// Componente principal com autenticação
const AppContent = () => {
  // IMPORTANTE: Usar isAdminOuDiretoria do contexto (função centralizada)
  const { usuario, authenticated, loading, login, logout, temPermissao, isAdminOuDiretoria } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mostrarPrimeiroAcesso, setMostrarPrimeiroAcesso] = useState(false);
  const [dropdownConfigAberto, setDropdownConfigAberto] = useState(false);
  const [dropdownClientesAberto, setDropdownClientesAberto] = useState(false);
  const [dropdownPedidosAberto, setDropdownPedidosAberto] = useState(false);
  const [secaoConfig, setSecaoConfig] = useState('usuarios');
  const [secaoClientes, setSecaoClientes] = useState('consulta');
  const dropdownRef = useRef(null);
  const dropdownClientesRef = useRef(null);
  const dropdownPedidosRef = useRef(null);

  // Função para verificar aba ativa baseada na rota
  const getAbaAtiva = () => {
    const path = location.pathname;
    if (path === '/' || path === '/inicio') return 'inicio';
    if (path.startsWith('/consulta-pedidos')) return 'pedidos';
    if (path === '/novo-pedido') return 'pedidos';
    if (path.startsWith('/clientes')) return 'clientes';
    if (path === '/dashboard') return 'dashboard';
    if (path === '/campanhas') return 'campanhas';
    if (path.startsWith('/configuracoes')) return 'configuracoes';
    return 'inicio';
  };

  const abaAtiva = getAbaAtiva();

  // ========================================
  // USAR FUNÇÃO CENTRALIZADA DO CONTEXTO
  // Isso garante consistência em todo o app
  // ========================================

  // Verificar se pode ver Campanhas (Admin, Diretoria, Administrativo ou com permissão específica)
  const podeVerCampanhas = () => {
    if (!usuario) return false;
    const dep = (usuario.departamento || '').toLowerCase().trim();
    
    // Admin, Diretoria e Administrativo sempre podem ver
    if (dep === 'admin' || dep === 'diretoria' || dep === 'administrativo' || dep === 'administrador') {
      return true;
    }
    // Outros usuários precisam da permissão específica
    return temPermissao('pode_ver_campanhas');
  };
  
  // Verificar se pode aprovar clientes
  const podeAprovarClientes = () => {
    if (!usuario) return false;
    const dep = (usuario.departamento || '').toLowerCase();
    return dep === 'admin' || dep === 'administrativo' || dep === 'diretoria' || 
           usuario.permissoes?.pode_aprovar_clientes === true;
  };

  // ========================================
  // FUNÇÃO DE NAVEGAÇÃO (para passar ao Home)
  // ========================================
  const navegarPara = (aba) => {
    // Mapeamento para as rotas
    if (aba === 'novo-pedido') {
      navigate('/novo-pedido');
    } else if (aba === 'pedidos') {
      navigate('/consulta-pedidos');
    } else if (aba === 'dashboard') {
      navigate('/dashboard');
    } else if (aba === 'campanhas') {
      navigate('/campanhas');
    } else if (aba === 'clientes') {
      navigate('/clientes');
    } else if (aba === 'configuracoes') {
      navigate('/configuracoes');
    } else {
      navigate('/');
    }
  };

  // ========================================
  // LISTENER PARA NAVEGAÇÃO PARA PEDIDO ESPECÍFICO
  // ========================================
  useEffect(() => {
    const handleNavegarParaPedido = (event) => {
      const { pedidoId } = event.detail;
      if (pedidoId) {
        // Navegar diretamente para a rota do pedido
        navigate(`/consulta-pedidos/${pedidoId}`);
      }
    };

    window.addEventListener('navegarParaPedido', handleNavegarParaPedido);
    return () => window.removeEventListener('navegarParaPedido', handleNavegarParaPedido);
  }, [navigate]);

  // ========================================
  // FECHAR DROPDOWN AO CLICAR FORA
  // ========================================
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownConfigAberto(false);
      }
      if (dropdownClientesRef.current && !dropdownClientesRef.current.contains(event.target)) {
        setDropdownClientesAberto(false);
      }
      if (dropdownPedidosRef.current && !dropdownPedidosRef.current.contains(event.target)) {
        setDropdownPedidosAberto(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ========================================
  // VERIFICAR SE É PRIMEIRO ACESSO
  // ========================================
  useEffect(() => {
    // Verificar se a URL é /primeiro-acesso (caso venha de redirecionamento)
    if (window.location.pathname === '/primeiro-acesso') {
      setMostrarPrimeiroAcesso(true);
      return;
    }

    // Verificar se o usuário precisa trocar senha
    if (authenticated && usuario?.precisa_trocar_senha === true) {
      setMostrarPrimeiroAcesso(true);
    } else {
      setMostrarPrimeiroAcesso(false);
    }
  }, [authenticated, usuario]);

  // Callback quando a senha é alterada com sucesso
  const handleSenhaAlterada = () => {
    setMostrarPrimeiroAcesso(false);
    // Limpar a URL se estiver em /primeiro-acesso
    if (window.location.pathname === '/primeiro-acesso') {
      window.history.pushState({}, '', '/');
    }
  };

  // Mostrar loading enquanto verifica autenticação
  if (loading) {
    return <Loading texto="Carregando..." tamanho="lg" fullScreen={true} />;
  }

  // Se não autenticado, mostrar tela de login
  if (!authenticated) {
    // Se está na URL de primeiro acesso mas não autenticado, redireciona para login
    return <Login onLoginSuccess={login} />;
  }

  // ========================================
  // AGUARDAR USUÁRIO ESTAR COMPLETAMENTE CARREGADO
  // Evita o bug de renderizar sem os dados completos
  // ========================================
  if (!usuario || !usuario.nome) {
    return <Loading texto="Carregando dados do usuário..." tamanho="lg" fullScreen={true} />;
  }

  // ========================================
  // SE PRECISA TROCAR SENHA, MOSTRAR TELA DE PRIMEIRO ACESSO
  // ========================================
  if (mostrarPrimeiroAcesso) {
    return <PrimeiroAcesso onSenhaAlterada={handleSenhaAlterada} />;
  }

  // Interface principal (usuário autenticado e não precisa trocar senha)
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* BARRA SUPERIOR FIXA */}
      <header 
        className="text-white shadow-md fixed top-0 left-0 right-0 z-50"
        style={{ 
          background: 'linear-gradient(135deg, #0D5DFC 0%, #0099cc 25%, #0D5DFC 50%, #0099cc 75%, #0D5DFC 100%)',
          backgroundSize: '400% 400%',
          animation: 'gradientShift 8s ease infinite'
        }}
      >
        <div className="px-10 py-4 flex justify-between items-center">
          {/* Logo e Nome do Sistema */}
          <div className="flex items-center gap-3">
            <div className="relative bg-white/25 backdrop-blur-xl border border-white/30 px-5 py-2.5 rounded-2xl shadow-lg flex items-center gap-4 overflow-hidden transition-all duration-300 hover:shadow-xl hover:bg-white/35 cursor-pointer group">
              {/* Efeito de brilho no hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              </div>
              {/* Nome do Sistema */}
              <div className="flex flex-col leading-tight relative z-10">
                <span className="text-xl font-bold text-white drop-shadow-md">
                  InovaFlow
                </span>
                <span className="text-[11px] text-white/90 tracking-wide font-medium">
                  Sistema de Gestão Empresarial
                </span>
              </div>
              {/* Barra Divisória */}
              <div className="w-px h-12 bg-white/40 relative z-10"></div>
              {/* Logo Primalia */}
              <img 
                src="/logo-primalia.svg" 
                alt="Primalia PVC" 
                className="h-14 w-auto object-contain relative z-10 brightness-0 invert"
              />
            </div>
          </div>

          {/* Menu */}
          <nav className="flex gap-2">
            <button
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                abaAtiva === 'inicio'
                  ? 'bg-white text-blue-600 font-semibold shadow'
                  : 'hover:bg-white/20'
              }`}
              onClick={() => navigate('/')}
            >
              <HomeIcon size={18} />
              Início
            </button>

            {/* Pedidos - Dropdown com opções */}
            {(temPermissao('pode_criar_pedido') || temPermissao('pode_ver_pedidos')) && (
              <div className="relative" ref={dropdownPedidosRef}>
                <button
                  className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                    abaAtiva === 'pedidos'
                      ? 'bg-white text-blue-600 font-semibold shadow'
                      : 'hover:bg-white/20'
                  }`}
                  onClick={() => setDropdownPedidosAberto(!dropdownPedidosAberto)}
                >
                  <ClipboardList size={18} />
                  Pedidos
                  <ChevronDown size={16} className={`transition-transform ${dropdownPedidosAberto ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Dropdown Menu */}
                {dropdownPedidosAberto && (
                  <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 py-2 min-w-[200px] z-50">
                    {temPermissao('pode_criar_pedido') && (
                      <button
                        onClick={() => {
                          navigate('/novo-pedido');
                          setDropdownPedidosAberto(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-all text-left whitespace-nowrap"
                      >
                        <FilePlus size={18} />
                        Novo Pedido
                      </button>
                    )}
                    {temPermissao('pode_ver_pedidos') && (
                      <button
                        onClick={() => {
                          navigate('/consulta-pedidos');
                          setDropdownPedidosAberto(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-all text-left whitespace-nowrap"
                      >
                        <ClipboardList size={18} />
                        Consulta Pedidos
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Clientes - Dropdown com opções */}
            <div className="relative" ref={dropdownClientesRef}>
              <button
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                  abaAtiva === 'clientes'
                    ? 'bg-white text-blue-600 font-semibold shadow'
                    : 'hover:bg-white/20'
                }`}
                onClick={() => setDropdownClientesAberto(!dropdownClientesAberto)}
              >
                <Building2 size={18} />
                Clientes
                <ChevronDown size={16} className={`transition-transform ${dropdownClientesAberto ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {dropdownClientesAberto && (
                <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 py-2 min-w-[200px] z-50">
                  <button
                    onClick={() => {
                      setSecaoClientes('consulta');
                      navigate('/clientes');
                      setDropdownClientesAberto(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-all text-left whitespace-nowrap"
                  >
                    <ClipboardList size={18} />
                    Consulta Clientes
                  </button>
                  {podeAprovarClientes() && (
                    <button
                      onClick={() => {
                        setSecaoClientes('aprovacoes');
                        navigate('/clientes');
                        setDropdownClientesAberto(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-all text-left whitespace-nowrap"
                    >
                      <ShieldCheck size={18} />
                      Aprovações Clientes
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Dashboard - Mostrar apenas se tiver permissão */}
            {temPermissao('pode_ver_dashboard') && (
              <button
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                  abaAtiva === 'dashboard'
                    ? 'bg-white text-blue-600 font-semibold shadow'
                    : 'hover:bg-white/20'
                }`}
                onClick={() => navigate('/dashboard')}
              >
                <BarChart3 size={18} />
                Dashboard
              </button>
            )}

            {/* Campanhas - Mostrar para Admin, Diretoria, Administrativo ou com permissão */}
            {podeVerCampanhas() && (
              <button
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                  abaAtiva === 'campanhas'
                    ? 'bg-white text-blue-600 font-semibold shadow'
                    : 'hover:bg-white/20'
                }`}
                onClick={() => navigate('/campanhas')}
              >
                <Trophy size={18} />
                Campanhas
              </button>
            )}

            {/* Configurações - Dropdown */}
            {(temPermissao('pode_aprovar_usuarios') || temPermissao('pode_gerenciar_tabelas') || isAdminOuDiretoria()) && (
              <div className="relative" ref={dropdownRef}>
                <button
                  className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                    abaAtiva === 'configuracoes'
                      ? 'bg-white text-blue-600 font-semibold shadow'
                      : 'hover:bg-white/20'
                  }`}
                  onClick={() => setDropdownConfigAberto(!dropdownConfigAberto)}
                >
                  <Settings size={18} />
                  Configurações
                  <ChevronDown size={16} className={`transition-transform ${dropdownConfigAberto ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Dropdown Menu */}
                {dropdownConfigAberto && (
                  <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 py-2 min-w-[200px] z-50">
                    {(temPermissao('pode_aprovar_usuarios') || isAdminOuDiretoria()) && (
                      <button
                        onClick={() => {
                          setSecaoConfig('usuarios');
                          navigate('/configuracoes');
                          setDropdownConfigAberto(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-all text-left"
                      >
                        <Users size={18} />
                        Gerenciar Usuários
                      </button>
                    )}
                    {(temPermissao('pode_gerenciar_tabelas') || isAdminOuDiretoria()) && (
                      <button
                        onClick={() => {
                          setSecaoConfig('tabelas');
                          navigate('/configuracoes');
                          setDropdownConfigAberto(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-all text-left"
                      >
                        <Table2 size={18} />
                        Gerenciar Tabelas
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* Informações do Usuário e Logout */}
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="font-semibold">{usuario?.nome}</p>
              <p className="text-xs text-blue-100">
                {usuario?.codigo_id} • {usuario?.regiao_acesso}
              </p>
            </div>
            <button
              onClick={logout}
              className="bg-white/20 hover:bg-white/30 p-2 rounded-lg transition-all"
              title="Sair"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* CONTEÚDO */}
      <main className="flex-grow pt-28 px-10 w-full">
        <Routes>
          {/* Tela inicial - Novo componente Home */}
          <Route path="/" element={<Home onNavigate={navegarPara} />} />
          <Route path="/inicio" element={<Navigate to="/" replace />} />

          {/* Novo Pedido */}
          <Route 
            path="/novo-pedido" 
            element={
              temPermissao('pode_criar_pedido') ? (
                <NovoPedido />
              ) : (
                <div className="text-center py-20">
                  <div className="bg-red-100 text-red-700 px-6 py-4 rounded-lg inline-block">
                    <p className="font-bold text-lg">Acesso Negado</p>
                    <p>Você não tem permissão para acessar esta seção.</p>
                  </div>
                </div>
              )
            } 
          />

          {/* Consulta de Pedidos */}
          <Route 
            path="/consulta-pedidos" 
            element={
              temPermissao('pode_ver_pedidos') ? (
                <ConsultaPedidos />
              ) : (
                <div className="text-center py-20">
                  <div className="bg-red-100 text-red-700 px-6 py-4 rounded-lg inline-block">
                    <p className="font-bold text-lg">Acesso Negado</p>
                    <p>Você não tem permissão para acessar esta seção.</p>
                  </div>
                </div>
              )
            } 
          />

          {/* Pedido Específico */}
          <Route 
            path="/consulta-pedidos/:numeroPedido" 
            element={
              temPermissao('pode_ver_pedidos') ? (
                <ConsultaPedidos />
              ) : (
                <div className="text-center py-20">
                  <div className="bg-red-100 text-red-700 px-6 py-4 rounded-lg inline-block">
                    <p className="font-bold text-lg">Acesso Negado</p>
                    <p>Você não tem permissão para acessar esta seção.</p>
                  </div>
                </div>
              )
            } 
          />

          {/* Clientes */}
          <Route path="/clientes" element={<Clientes secaoInicial={secaoClientes} />} />

          {/* Dashboard */}
          <Route 
            path="/dashboard" 
            element={
              temPermissao('pode_ver_dashboard') ? (
                <Dashboard />
              ) : (
                <div className="text-center py-20">
                  <div className="bg-red-100 text-red-700 px-6 py-4 rounded-lg inline-block">
                    <p className="font-bold text-lg">Acesso Negado</p>
                    <p>Você não tem permissão para acessar esta seção.</p>
                  </div>
                </div>
              )
            } 
          />

          {/* Campanhas */}
          <Route 
            path="/campanhas" 
            element={
              podeVerCampanhas() ? (
                <Campanhas />
              ) : (
                <div className="text-center py-20">
                  <div className="bg-red-100 text-red-700 px-6 py-4 rounded-lg inline-block">
                    <p className="font-bold text-lg">Acesso Negado</p>
                    <p>Você não tem permissão para acessar esta seção.</p>
                  </div>
                </div>
              )
            } 
          />

          {/* Configurações */}
          <Route 
            path="/configuracoes" 
            element={
              (temPermissao('pode_aprovar_usuarios') || temPermissao('pode_gerenciar_tabelas') || isAdminOuDiretoria()) ? (
                <Configuracoes secaoInicial={secaoConfig} />
              ) : (
                <div className="text-center py-20">
                  <div className="bg-red-100 text-red-700 px-6 py-4 rounded-lg inline-block">
                    <p className="font-bold text-lg">Acesso Negado</p>
                    <p>Você não tem permissão para acessar esta seção.</p>
                  </div>
                </div>
              )
            } 
          />

          {/* Rota padrão - redireciona para home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {/* FOOTER GLOBAL */}
      <Footer />
    </div>
  );
};

// Wrapper com Providers (Auth + Modal)
const App = () => {
  return (
    <AuthProvider>
      <ModalProvider>
        <AppContent />
      </ModalProvider>
    </AuthProvider>
  );
};

export default App;